import { useQuery } from '@tanstack/react-query';

import type {
  ClmWorkloadFilter,
  ClmFilterFieldEnum,
  ClmListFilterValuesResponse,
} from '@cast/types';
import { QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { makeIsEnabled } from '@cast/utils';

const CLM_FILTER_VALUES_QUERY_KEY = 'clm-filter-values';

const getClmFilterValuesQueryKey = ({
  clusterId,
  filter,
  field,
  keyword,
}: {
  clusterId?: string;
  filter?: ClmWorkloadFilter;
  field?: ClmFilterFieldEnum;
  keyword?: string;
}) => [CLM_FILTER_VALUES_QUERY_KEY, clusterId, field, keyword, filter];

type Params = {
  clusterId: string;
  filter: ClmWorkloadFilter;
  field: ClmFilterFieldEnum;
  keyword?: string;
};

type UseClmFilterValuesQueryArgs = {
  params: Params;
  options?: QueryOptions<ClmListFilterValuesResponse>;
};

export const useClmFilterValuesQuery = ({
  params,
  options,
}: UseClmFilterValuesQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const organizationId = currentOrganization?.id;
  const { clusterId, filter, field, keyword } = params;

  return useQuery({
    queryKey: getClmFilterValuesQueryKey({
      clusterId,
      filter,
      field,
      keyword,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.listContainerLiveMigrationFilterValues({
          organizationId: organizationId!,
          clusterId: clusterId,
          containerLiveMigrationFilter: filter,
          field,
          keyword,
        });

      return data;
    },
    ...options,
    enabled: makeIsEnabled(!!organizationId, !!clusterId, options?.enabled),
  });
};
